import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/base/StatusBadge";
import { fmtDateTime } from "@/lib/format";

// Read-only presentation of a product's change history and order activity.
// No state, no callbacks, no data fetching — it renders data already resolved
// by the parent ProductDetail page.

interface HistoryRow {
  id: string;
  field: string;
  old_value: string | null;
  new_value: string | null;
  variant_id: string | null;
  created_at: string;
}

interface ActivityPurchase {
  id: string;
  purchase_order_id: string;
  qty_ordered: number;
  qty_received: number;
  po: {
    id: string;
    po_number: string;
    status: string;
    order_date: string | null;
    supplier_id: string | null;
  };
}

interface ActivitySale {
  id: string;
  sales_order_id: string;
  qty_ordered: number;
  qty_fulfilled: number;
  so: {
    id: string;
    order_number: string;
    status: string;
    order_date: string | null;
    customer_id: string | null;
  };
}

interface ActivityData {
  purchases: ActivityPurchase[];
  sales: ActivitySale[];
  supplierName: Record<string, string>;
  customerName: Record<string, string>;
}

interface Props {
  historyData?: HistoryRow[];
  activityData?: ActivityData;
}

export default function HistoryTab({ historyData, activityData }: Props) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">Change History</h3>
        </div>
        {(historyData ?? []).length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">No recorded changes</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {historyData!.map((h) => (
              <div key={h.id} className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700">{h.field}</span>
                  {h.variant_id && <span className="text-[10px] text-slate-300 font-mono uppercase">variant</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {h.old_value != null && h.old_value !== "" ? <span className="text-slate-400 line-through">{h.old_value}</span> : null}
                  {h.old_value != null && h.old_value !== "" && h.new_value != null && h.new_value !== "" ? <span className="text-slate-300"> → </span> : null}
                  {h.new_value != null && h.new_value !== "" ? <span className="text-slate-700">{h.new_value}</span> : null}
                  <span className="text-slate-300"> · {fmtDateTime(h.created_at)}</span>
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">Purchasing &amp; Sales Activity</h3>
        </div>
        {!activityData || ((activityData.purchases?.length ?? 0) === 0 && (activityData.sales?.length ?? 0) === 0) ? (
          <div className="px-4 py-12 text-center text-sm text-slate-400">No orders reference this product yet</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {activityData.purchases.map((it) => (
              <Link key={`po-${it.id}`} to={`/purchase-orders/${it.po.id}`} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                <div className="min-w-0">
                  <span className="text-sm font-semibold text-slate-800 font-mono">{it.po.po_number}</span>
                  <span className="text-xs text-slate-400 ml-2">{activityData.supplierName[it.po.supplier_id ?? ""] ?? "—"}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-slate-500">{it.qty_received} / {it.qty_ordered} received</span>
                  <StatusBadge status={it.po.status} />
                </div>
              </Link>
            ))}
            {activityData.sales.map((it) => (
              <Link key={`so-${it.id}`} to={`/sales-orders/${it.so.id}`} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                <div className="min-w-0">
                  <span className="text-sm font-semibold text-slate-800 font-mono">{it.so.order_number}</span>
                  <span className="text-xs text-slate-400 ml-2">{activityData.customerName[it.so.customer_id ?? ""] ?? "—"}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-slate-500">{it.qty_fulfilled} / {it.qty_ordered} fulfilled</span>
                  <StatusBadge status={it.so.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}